/*
 * Insights V1 presentation helpers. Formatting and labelling only.
 *
 * The backend is the analytics engine (blueprint §7.1, §5, §15.2): due work,
 * Prescribed vs Extra attribution, set matching, week attribution, cycle
 * selection, ratios and muscle contribution all arrive computed. Nothing here
 * re-derives them; helpers read the response fields and format them.
 */

import { CURRENT_ANALYSIS } from "../api/insightsApi";

export const TIMEFRAME_LABELS = Object.freeze({
  [CURRENT_ANALYSIS]: "Current Analysis",
  "3M": "3 months",
  "6M": "6 months",
  "1Y": "1 year",
  "2Y": "2 years",
  "5Y": "5 years",
  "10Y": "10 years",
  ALL_TIME: "All time",
});

export const WEEK_STATUS = Object.freeze({
  COMPLETED: "COMPLETED",
  IN_PROGRESS: "IN_PROGRESS",
  UPCOMING: "UPCOMING",
});

export const CYCLE_ROLE = Object.freeze({ ACTIVE: "ACTIVE", PREVIOUS: "PREVIOUS" });

// Ratios stay fractions end to end; only the label is a percentage.
// 0.874 → "87%", 1 → "100%", 1.17 → "117%". A null ratio (nothing due) is
// "no data", never 0 %: it returns null and the caller renders a neutral state.
export function formatRatioPercent(ratio) {
  if (ratio === null || ratio === undefined) return null;
  const numeric = Number(ratio);
  if (!Number.isFinite(numeric)) return null;
  return `${Math.round(numeric * 100)}%`;
}

// Natural set counts (blueprint §5): 12 → "12", 12.5 → "12.5", never "12.0".
export function formatSetCount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  return String(Math.round(numeric * 10) / 10);
}

const SHORT_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const SHORT_MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "2-digit",
  timeZone: "UTC",
});

function parseDateKey(dateKey) {
  if (typeof dateKey !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Date keys are calendar dates, not instants: formatted in UTC so the label is
// the key itself whatever the device offset.
export function formatShortDate(dateKey) {
  const date = parseDateKey(dateKey);
  return date ? SHORT_DATE.format(date) : "";
}

export function formatMonth(dateKey, { withYear = false } = {}) {
  const date = parseDateKey(dateKey);
  if (!date) return "";
  return withYear ? MONTH_YEAR.format(date) : SHORT_MONTH.format(date);
}

export function formatDateRange(startDate, endDate) {
  const start = formatShortDate(startDate);
  const end = formatShortDate(endDate);
  if (!start || !end) return "";
  return `${start} – ${end}`;
}

export function formatWeekStatus(status) {
  if (status === WEEK_STATUS.IN_PROGRESS) return "in progress";
  if (status === WEEK_STATUS.UPCOMING) return "upcoming";
  return "completed";
}

export function cycleRoleLabel(role) {
  return role === CYCLE_ROLE.PREVIOUS ? "Previous cycle" : "Current cycle";
}

// Current Analysis keeps the API's cycle order semantics but is shown in time
// order: previous cycle first, then the active one.
export function orderCyclesForTimeline(cycles = []) {
  const previous = cycles.filter((cycle) => cycle?.role === CYCLE_ROLE.PREVIOUS);
  const active = cycles.filter((cycle) => cycle?.role === CYCLE_ROLE.ACTIVE);
  return [...previous, ...active];
}

export function findCycle(cycles = [], role) {
  return cycles.find((cycle) => cycle?.role === role) || null;
}

/*
 * One chart column per week, in time order. Current Analysis columns keep the
 * cycle-timeline identity (W1, W2, … per cycle); historical columns keep the
 * API calendar weeks. The two models are never mixed in one series.
 */
export function buildTimelineColumns(response) {
  if (!response || response.available === false) return [];
  if (response.timeframe === CURRENT_ANALYSIS) {
    return orderCyclesForTimeline(response.cycles || []).flatMap((cycle) => (cycle.weeks || []).map((week) => ({
      key: `${cycle.role}-${week.weekNumber}`,
      label: `W${week.weekNumber}`,
      // Bare week number for dense charts, where the cycle is named above.
      shortLabel: String(week.weekNumber),
      longLabel: `${cycleRoleLabel(cycle.role)}, week ${week.weekNumber}`,
      segment: cycle.role,
      status: week.status,
      week,
      cycle,
    })));
  }
  return (response.weeks || []).map((week) => ({
    key: week.startDate,
    label: formatShortDate(week.startDate),
    longLabel: `Week of ${formatShortDate(week.startDate)}`,
    segment: null,
    status: week.status,
    week,
    cycle: null,
  }));
}

/* Muscle labels ------------------------------------------------------------ */

const MUSCLE_LABELS = Object.freeze({
  chest: "Chest",
  back: "Back",
  lats: "Lats",
  upper_back: "Upper Back",
  traps: "Traps",
  shoulders: "Shoulders",
  front_delts: "Front Delts",
  side_delts: "Side Delts",
  rear_delts: "Rear Delts",
  biceps: "Biceps",
  triceps: "Triceps",
  forearms: "Forearms",
  quads: "Quads",
  hamstrings: "Hamstrings",
  glutes: "Glutes",
  glute_max: "Glute Max",
  glute_med: "Glute Med",
  adductors: "Adductors",
  calves: "Calves",
  gastrocnemius: "Gastrocnemius",
  soleus: "Soleus",
  core: "Core",
  abs: "Abs",
  obliques: "Obliques",
  lower_back: "Lower Back",
});

export function muscleLabel(key) {
  if (MUSCLE_LABELS[key]) return MUSCLE_LABELS[key];
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export const VOLUME_BASIS = Object.freeze({
  WEIGHTED: "weightedSets",
  DIRECT: "directSets",
  INDIRECT: "indirectSets",
});

/*
 * The muscle groups to list, read from a volume body: the API's `nodes` order
 * (the canonical contribution order) and its `parents[].children`. A parent
 * appears where its first child would; single nodes stand alone. Values are
 * read from the API row (a parent row for a parent) and never summed here.
 */
export function buildMuscleGroups(body) {
  if (!body) return [];
  const parents = Array.isArray(body.parents) ? body.parents : [];
  const parentByChild = new Map();
  parents.forEach((parent) => (parent.children || []).forEach((child) => parentByChild.set(child, parent)));
  const groups = [];
  const seen = new Set();
  for (const row of body.nodes || []) {
    const parent = parentByChild.get(row.node);
    if (!parent) {
      groups.push({ key: row.node, kind: "node", children: [] });
      continue;
    }
    if (seen.has(parent.parent)) continue;
    seen.add(parent.parent);
    groups.push({ key: parent.parent, kind: "parent", children: [...(parent.children || [])] });
  }
  return groups;
}

// One muscle's value in a volume body for the chosen basis: the parent row
// for a parent key, the node row otherwise. A missing body (no planned
// occurrence that week) is null — no plan, not a planned zero.
export function readMuscleValue(body, key, basis = VOLUME_BASIS.WEIGHTED) {
  if (!body) return null;
  const parent = (body.parents || []).find((row) => row.parent === key);
  const row = parent || (body.nodes || []).find((node) => node.node === key);
  if (!row) return 0;
  const value = Number(row[basis]);
  return Number.isFinite(value) ? value : 0;
}
